import React, { useEffect, useState } from "react";
import ResponsiveImg, { SIZES } from "../components/ResponsiveImg";
import Link from "../components/LocalizedLink";
import { useI18n } from "../i18n/I18nProvider";
import SchemaMarkup, { buildBreadcrumb } from "../components/SchemaMarkup";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { api, resolveImage } from "../lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const { t } = useI18n();
  useDocumentMeta({
    title: t("blog_meta_title"),
    description: t("blog_meta_desc"),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/blog");
        setPosts(data);
      } catch (_err) { /* ignore */ }
    })();
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div className="bg-[#F9F6F0] text-[#1A1A1A]">
      <SchemaMarkup schema={buildBreadcrumb([{ name: t("home"), url: "/" }, { name: t("blog_breadcrumb"), url: "/blog" }])} />

      <section className="pt-40 pb-12 lg:pb-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-5">— {t("blog_eyebrow")}</p>
          <h1 data-testid="blog-title" className="text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] max-w-5xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t("blog_heading")}
          </h1>
        </div>
      </section>

      {featured && (
        <section className="pb-16 lg:pb-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
            <Link to={`/blog/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-7 aspect-[16/10] overflow-hidden">
                <ResponsiveImg src={featured.image} sizes={SIZES.featured} priority alt={featured.title} className="w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-[1.04]" />
              </div>
              <div className="lg:col-span-5">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">{t("blog_featured")} · {featured.readTime}</p>
                <h2 className="text-3xl lg:text-5xl font-light leading-tight mb-5 group-hover:text-[#C5A880] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {featured.title}
                </h2>
                <p className="text-base text-[#3D3B38] leading-relaxed mb-6">{featured.excerpt}</p>
                <span className="text-[11px] tracking-[0.25em] uppercase border-b border-[#1A1A1A] pb-1 group-hover:text-[#C5A880] group-hover:border-[#C5A880] transition-colors">
                  {t("blog_read_article")}
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section className="pb-24 lg:pb-32 bg-[#FFFFFF] border-y border-[#E5DFD5] py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {rest.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group flex flex-col border-b border-[#E5DFD5] pb-10">
                <div className="aspect-[4/3] overflow-hidden mb-6">
                  <ResponsiveImg src={p.image} sizes={SIZES.card} alt={p.title} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]" />
                </div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-3">{p.readTime}</p>
                <h3 className="text-2xl lg:text-3xl font-light leading-tight mb-3 group-hover:text-[#C5A880] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {p.title}
                </h3>
                <p className="text-base text-[#73706B] leading-relaxed line-clamp-3">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
